import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Star, Video, MessageSquare, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoctorsPage = () => {
  const { getAuthHeader } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [bookingData, setBookingData] = useState({
    slot_date: '',
    slot_time: '',
    consultation_type: 'video',
    symptoms: '',
  });

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const handleBookConsultation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/consultations`,
        {
          doctor_id: selectedDoctor.id,
          ...bookingData,
        },
        { headers: getAuthHeader() }
      );
      toast.success('Consultation booked successfully!');
      setSelectedDoctor(null);
      setBookingData({ slot_date: '', slot_time: '', consultation_type: 'video', symptoms: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="doctors-page">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Consult Ayurvedic Experts
          </h1>
          <p className="text-lg text-gray-600">Connect with verified practitioners for personalized care</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {doctors.map((doctor) => (
            <Card key={doctor.id} className="card-lift">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">Dr. {doctor.name}</CardTitle>
                    <p className="text-sm text-gray-600">{doctor.profile.specialization}</p>
                  </div>
                  {doctor.profile.verified && (
                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      Verified
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center space-x-1 text-sm">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <span className="font-medium">{doctor.rating.toFixed(1)}</span>
                    <span className="text-gray-500">({doctor.total_consultations} consultations)</span>
                  </div>

                  <div className="text-sm text-gray-600">
                    <div><strong>{doctor.profile.experience_years}</strong> years experience</div>
                    <div className="mt-1"><strong>Languages:</strong> {doctor.profile.languages.join(', ')}</div>
                  </div>

                  <div className="text-2xl font-bold text-primary">
                    ₹{doctor.profile.consultation_fee}
                    <span className="text-sm font-normal text-gray-500">/session</span>
                  </div>

                  <Button
                    onClick={() => setSelectedDoctor(doctor)}
                    className="w-full"
                    data-testid={`book-doctor-${doctor.id}`}
                  >
                    Book Consultation
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {doctors.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500">No doctors available at the moment</p>
          </div>
        )}
      </main>

      <Footer />

      {/* Booking Dialog */}
      <Dialog open={!!selectedDoctor} onOpenChange={() => setSelectedDoctor(null)}>
        <DialogContent data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle>Book Consultation</DialogTitle>
            {selectedDoctor && (
              <p className="text-sm text-gray-600">Dr. {selectedDoctor.name} - {selectedDoctor.profile.specialization}</p>
            )}
          </DialogHeader>

          <form onSubmit={handleBookConsultation} className="space-y-4">
            <div>
              <Label htmlFor="slot_date">Consultation Date</Label>
              <Input
                id="slot_date"
                type="date"
                data-testid="booking-date-input"
                value={bookingData.slot_date}
                onChange={(e) => setBookingData({ ...bookingData, slot_date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="slot_time">Preferred Time</Label>
              <Input
                id="slot_time"
                type="time"
                data-testid="booking-time-input"
                value={bookingData.slot_time}
                onChange={(e) => setBookingData({ ...bookingData, slot_time: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="consultation_type">Consultation Type</Label>
              <select
                id="consultation_type"
                data-testid="booking-type-select"
                className="w-full border border-gray-200 rounded-lg h-10 px-3"
                value={bookingData.consultation_type}
                onChange={(e) => setBookingData({ ...bookingData, consultation_type: e.target.value })}
              >
                <option value="video">Video Call</option>
                <option value="chat">Chat</option>
              </select>
            </div>

            <div>
              <Label htmlFor="symptoms">Symptoms / Concerns (Optional)</Label>
              <textarea
                id="symptoms"
                data-testid="booking-symptoms-input"
                className="w-full border border-gray-200 rounded-lg p-3 min-h-[100px]"
                value={bookingData.symptoms}
                onChange={(e) => setBookingData({ ...bookingData, symptoms: e.target.value })}
                placeholder="Describe your health concerns..."
              />
            </div>

            <Button type="submit" className="w-full" data-testid="confirm-booking-btn">
              Confirm Booking
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorsPage;